import React, {useMemo, useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import {useLanguage} from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiExternalLink, FiStar, FiChevronDown, FiChevronUp} = FiIcons;

const ResourceCard = ({resource, index, onTagClick, viewMode = 'expanded', forceExpanded = false}) => {
  const {t, language} = useLanguage();
  const [isExpanded, setIsExpanded] = useState(viewMode === 'expanded' || forceExpanded);

  // Update expansion state when viewMode or forceExpanded changes
  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    } else {
      // Respect the viewMode setting
      setIsExpanded(viewMode === 'expanded');
    }
  }, [viewMode, forceExpanded]);

  const handleClick = () => {
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  const handleTagClick = (tag, e) => {
    e.stopPropagation();
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  const handleToggleExpanded = (e) => {
    e.stopPropagation();
    if (!forceExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  const translateCategory = useMemo(() => {
    return (category) => {
      const categoryMap = {
        'Investigación': t('investigacion'),
        'Texto y redacción': t('texto-redaccion'),
        'Educación': t('educacion'),
        'Chatbots': t('chatbots'),
        'Video': t('video'),
        'Mapas mentales': t('mapas-mentales'),
        'Imágenes': t('imagenes'),
        'Diseño': t('diseno'),
        'Generador de asistentes virtuales': t('generador-asistentes'),
        'Detector de contenidos de IA': t('detector-contenidos'),
        'Música': t('musica'),
        'Motores de búsqueda': t('motores-busqueda'),
        'Audio': t('audio'),
        'Redes sociales': t('redes-sociales')
      };
      return categoryMap[category] || category;
    };
  }, [t]);

  // Get description in current language
  const getDescription = useMemo(() => {
    if (resource.description && typeof resource.description === 'object') {
      return resource.description[language] || resource.description['es'] || resource.description;
    }
    return resource.description || '';
  }, [resource.description, language]);

  // Get tags in current language
  const getTags = useMemo(() => {
    if (resource.tags && typeof resource.tags === 'object') {
      return resource.tags[language] || resource.tags['es'] || resource.tags;
    }
    return resource.tags || [];
  }, [resource.tags, language]);

  // Get emoji for category
  const getCategoryEmojis = (category) => {
    const emojiMap = {
      'Audio': ['🎙️', '🔊'],
      'Chatbots': ['🤖', '💬'],
      'Detector de contenidos de IA': ['🔎', '🤖'],
      'Diseño': ['🎨', '🖌️'],
      'Educación': ['🎓', '📚'],
      'Generador de asistentes virtuales': ['🗣️', '🤖'],
      'Imágenes': ['🖼️', '📷'],
      'Investigación': ['🔬', '📚'],
      'Mapas mentales': ['🧠', '🗺️'],
      'Música': ['🎵', '🎶'],
      'Motores de búsqueda': ['🔎', '🌐'],
      'Redes sociales': ['📱', '💬'],
      'Texto y redacción': ['📝', '✍️'],
      'Video': ['🎬', '🎥']
    };
    const emojis = emojiMap[category] || ['🤖', '⚡'];
    return emojis[index % 2];
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      transition={{delay: index * 0.05}}
      whileHover={{y: -2, scale: 1.01}}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col ${
        isExpanded ? 'h-full' : 'h-auto'
      }`}
    >
      {/* Header - Always Visible */}
      <div 
        className="p-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 min-h-[80px]"
        onClick={viewMode === 'compact' && !forceExpanded ? handleToggleExpanded : handleClick}
      >
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {/* Category Emoji */}
          <div className="text-xl flex-shrink-0">
            {getCategoryEmojis(resource.category)}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2 leading-tight">
              {resource.name}
            </h3>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={handleClick}
            className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
            title={`Abrir ${resource.name}`}
          >
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
          </button>
          
          {viewMode === 'compact' && !forceExpanded && (
            <button
              onClick={handleToggleExpanded}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Expandable Content */}
      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{duration: 0.3, ease: 'easeInOut'}}
        className="overflow-hidden"
        style={{
          height: isExpanded ? 'auto' : 0
        }}
      >
        <div className="p-4 pt-0 flex flex-col">
          {/* Description - Fixed height */}
          <div className="mb-4">
            <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 h-[60px] overflow-hidden">
              {getDescription}
            </p>
          </div>

          {/* Category badge */}
          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full truncate max-w-full">
              {translateCategory(resource.category)}
            </span>
          </div>

          {/* Tags - Fixed height */}
          {getTags.length > 0 && (
            <div className="mb-4 h-[60px] overflow-hidden">
              <div className="flex flex-wrap gap-1">
                {getTags.slice(0, 6).map((tag, tagIndex) => (
                  <button
                    key={tagIndex}
                    onClick={(e) => handleTagClick(tag, e)}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-300 transition-colors truncate max-w-[120px]"
                    title={tag}
                  >
                    {tag}
                  </button>
                ))}
                {getTags.length > 6 && (
                  <span className="px-2 py-1 text-gray-400 text-xs">
                    +{getTags.length - 6}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Rating if available */}
          {resource.rating && (
            <div className="flex items-center space-x-1 mt-auto">
              <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {resource.rating}
              </span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ResourceCard;